import { parseCalendarDate } from '../../../utils/formatters';

// TEAM-CAL-UI-001: presentation helpers only — dates/chargeability rules mirror LeaveCalendar.jsx exactly.
export const PALETTE = {
  casual: { label: 'Casual', bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6', avatar: '#2563eb' },
  sick: { label: 'Sick', bg: '#ffe4e6', text: '#be123c', dot: '#f43f5e', avatar: '#e11d48' },
  planned: { label: 'Planned', bg: '#dcfce7', text: '#15803d', dot: '#22c55e', avatar: '#16a34a' },
  emergency: { label: 'Emergency', bg: '#fef3c7', text: '#b45309', dot: '#f59e0b', avatar: '#d97706' },
  maternity: { label: 'Maternity', bg: '#ede9fe', text: '#6d28d9', dot: '#8b5cf6', avatar: '#7c3aed' },
  earned: { label: 'Earned', bg: '#e0e7ff', text: '#4338ca', dot: '#6366f1', avatar: '#4f46e5' },
  compensatory: { label: 'Compensatory', bg: '#cffafe', text: '#0e7490', dot: '#06b6d4', avatar: '#0891b2' },
  unpaid: { label: 'Loss of Pay', bg: '#f1f5f9', text: '#475569', dot: '#94a3b8', avatar: '#64748b' },
};
export const FALLBACK = { label: 'Leave', bg: '#f1f5f9', text: '#334155', dot: '#94a3b8', avatar: '#64748b' };
export const paletteFor = (type) => PALETTE[type] || { ...FALLBACK, label: (type || 'Leave').replace(/_/g, ' ') };
export const typeLabel = (type) => `${paletteFor(type).label} Leave`.replace('Loss of Pay Leave', 'Loss of Pay');

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const todayIso = () => iso(new Date());
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/** 6-row × 7-col grid including leading/trailing days of adjacent months (isCurrent=false). */
export const monthGrid = (year, month) => {
  const first = new Date(year, month - 1, 1);
  const start = addDays(first, -first.getDay());
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(start, i);
    cells.push({ date: d, iso: iso(d), day: d.getDate(), isCurrent: d.getMonth() === month - 1, dow: d.getDay() });
  }
  // drop a fully-adjacent trailing week
  return cells.slice(-7).every(c => !c.isCurrent) ? cells.slice(0, 35) : cells;
};

export const weekGrid = (anchor) => {
  const start = addDays(anchor, -anchor.getDay());
  return Array.from({ length: 7 }, (_, i) => { const d = addDays(start, i); return { date: d, iso: iso(d), day: d.getDate(), isCurrent: true, dow: d.getDay() }; });
};

/** Same range test as LeaveCalendar.getLeavesForDate (local-midnight wall clock). */
export const leavesOn = (entries, dateIso) => {
  const cur = parseCalendarDate(dateIso);
  if (!cur) return [];
  // LEAVE-SANDWICH-002: a request also "covers" the intervening holiday/weekly-off dates charged to it
  return entries.filter(e => { if (isBridgeDate(e, dateIso)) return true; const s = parseCalendarDate(e.start_date), en = parseCalendarDate(e.end_date); return s && en && cur >= s && cur <= en; });
};

/** LEAVE-SANDWICH-002 — intervening holiday/weekly-off dates between two continuous requests, charged to the later one. */
export const isBridgeDate = (e, dateIso) => Array.isArray(e.sandwich_bridge_dates) && e.sandwich_bridge_dates.includes(dateIso);
export const bridgeDays = (e) => Number(e.sandwich_bridge_days) || 0;
export const baseDays = (e) => (typeof e.base_days === 'number' ? e.base_days : (typeof e.total_days === 'number' ? e.total_days - bridgeDays(e) : null));

/** LEAVE-SANDWICH-001 — identical to LeaveCalendar: chargeable inside first→last working-day window. */
export const isChargeable = (leave, dateIso) => (leave.chargeable_start ? (dateIso >= leave.chargeable_start && dateIso <= leave.chargeable_end) : true);

export const initials = (name = '') => name.trim().split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase() || 'U';
// LEAVE-CALENDAR-PENDING-VISIBILITY-001: pending entries are informational; confirmed = approved only.
export const isPending = (e) => e.status === 'pending';
export const approvedOnly = (entries) => entries.filter(e => !isPending(e));
export const STATUS_STYLE = { pending: { label: 'Pending', text: 'text-amber-700', bg: 'bg-amber-50', ring: 'ring-amber-300' }, approved: { label: 'Approved', text: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'ring-emerald-300' } };
export const statusStyle = (e) => STATUS_STYLE[isPending(e) ? 'pending' : 'approved'];
export const firstName = (name = '') => name.split(' ')[0] || 'User';
export const durationLabel = (e) => {
  if (e.day_part && e.day_part !== 'full') return 'Half day';
  const n = typeof e.total_days === 'number' ? e.total_days : null;
  if (n == null) return '—';
  const b = bridgeDays(e);
  return b > 0 ? `${n} day${n === 1 ? '' : 's'} (${baseDays(e)} + ${b} sandwich)` : `${n} day${n === 1 ? '' : 's'}`;
};
